/**
 * BadgeBar (N2) — the top-bar awareness layer: four badged icons that poll a
 * lightweight counts endpoint every ~3s and open dropdowns of recent items.
 *
 *   New Leads      — company-wide unclaimed; opens the All Clients → New Leads tab
 *   Notifications  — unread, owner/admin-scoped; mark-read is deliberate
 *   Follow-ups     — the caller's follow-ups due today; opens the lead
 *   Messages       — UNREAD inbound client email on leads you own (admin: all);
 *                    opens the lead's Sales feed at that message, expanded
 *
 * Counts are role/owner-aware server-side. Full lists load only when a dropdown
 * opens (we don't refetch lists every 3s). Polling, not realtime subscriptions.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApiClient } from '../hooks/useApi';
import { formatDateTime } from '../lib/utils';
import { userTz, todayInTz, shortTime, clockInTz, formatDateTimeInTz } from '../lib/time';
import { Icons } from '../lib/icons';
import { formatJobNo } from '../lib/jobNumber';
import { leadsOfFollowUps } from '../lib/followUpLeads';
import { MissedCallLookup } from './MissedCallLookup';
import { CallNotesModal } from './CallNotesModal';
import type { RingCentralCallLogged } from '../services/api';

interface BadgeBarProps {
  onSelectLead: (leadId: string) => void;
  /** Open a lead's Sales feed scrolled to one email, expanded (G1d). */
  onOpenMessage: (leadId: string, messageId: string) => void;
  onGoToNewLeads: () => void;
  onGoToNotifications: () => void;
  onGoToFollowUps: () => void;
}

type Panel = 'newLeads' | 'notifications' | 'followups' | 'messages' | null;

const POLL_MS = 3000;

const EMPTY_COUNTS = {
  newLeads: 0, notificationsUnread: 0, messages: 0,
  followupsToday: 0, followupsGeneralToday: 0, followupsTimedToday: 0, followupsOverdue: 0,
};
export type BadgeCounts = typeof EMPTY_COUNTS;

/** Badge number = general-today + overdue + timed-today (server-computed counts). */
export const followupBadgeOf = (c: BadgeCounts) => c.followupsGeneralToday + c.followupsOverdue + c.followupsTimedToday;

/**
 * THE LIVE COUNTS — polled every ~3s (plus once immediately). One source for
 * the desktop BadgeBar and the phone bottom bar, so the two cannot disagree.
 * `enabled` false stops polling (the phone bar passes it only at phone width,
 * where BadgeBar is not mounted — one poller at a time).
 */
export function useBadgeCounts(enabled = true): [BadgeCounts, React.Dispatch<React.SetStateAction<BadgeCounts>>] {
  const api = useApiClient();
  const { user } = useAuth();
  const tz = userTz(user);
  const [counts, setCounts] = useState<BadgeCounts>(EMPTY_COUNTS);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const tick = async () => {
      try {
        const res = await api.getBadges(todayInTz(tz));
        if (alive && res.data) setCounts(res.data);
      } catch {
        /* transient — keep last counts */
      }
    };
    tick();
    const id = window.setInterval(tick, POLL_MS);
    // Something cleared a count elsewhere (a feed line's "Mark read"): now, not next poll.
    window.addEventListener('crm:badges-changed', tick);
    return () => {
      alive = false;
      window.clearInterval(id);
      window.removeEventListener('crm:badges-changed', tick);
    };
  }, [api, tz, enabled]);
  return [counts, setCounts];
}

export function BadgeBar({ onSelectLead, onOpenMessage, onGoToNewLeads, onGoToNotifications, onGoToFollowUps }: BadgeBarProps) {
  const api = useApiClient();
  const { user } = useAuth();
  const tz = userTz(user);
  // setCounts: marking a notification read lowers the badge before the next poll.
  const [counts, setCounts] = useBadgeCounts();
  const followupBadge = followupBadgeOf(counts);
  const [open, setOpen] = useState<Panel>(null);
  const [lookupCallId, setLookupCallId] = useState<string | null>(null);
  const [lookup, setLookup] = useState<{ kind: 'text' | 'call'; id: string } | null>(null);
  // An "Add notes" item (a RingCentral app / desk-phone call the server logged): its notes modal.
  const [notesFor, setNotesFor] = useState<RingCentralCallLogged | null>(null);
  const [list, setList] = useState<any[]>([]);
  const [listLoading, setListLoading] = useState(false);

  const loadList = async (panel: Exclude<Panel, null>) => {
    setListLoading(true);
    setList([]);
    try {
      if (panel === 'newLeads') {
        // Unclaimed New Leads, newest first, from the server (paging 3/4).
        setList((await api.searchLeads({ unclaimed: true, sort: 'created', dir: 'desc', page_size: 20 })).data?.rows ?? []);
      } else if (panel === 'messages') {
        setList((await api.getUnreadMessages(20)).data?.items ?? []);
      } else if (panel === 'notifications') {
        const d = (await api.getNotifications({ limit: 15 })).data;
        setList((d?.notifications || []).slice(0, 15));
      } else if (panel === 'followups') {
        // Section 3 rows: today's TIMED follow-ups for me, chronological. Job#
        // and name come with each follow-up (paging 3/4). A row whose time has
        // passed today is marked past-due but stays in this list.
        const fus = (await api.getFollowUps()).data || [];
        const leadById = leadsOfFollowUps(fus);
        const t = todayInTz(tz);
        const nowHHMM = clockInTz(tz).time;
        const rows = (fus as any[])
          .filter((f) => f.assigned_to_user_id === user?.id && f.follow_up_date === t && f.follow_up_time != null && f.completed !== true)
          .sort((a, b) => String(a.follow_up_time).localeCompare(String(b.follow_up_time)))
          .map((f) => {
            const lead = leadById.get(f.lead_id);
            return {
              id: f.id,
              lead_id: f.lead_id,
              job: formatJobNo(lead),  // em-dash when the lead row hasn't loaded
              name: lead ? `${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim() : 'Lead',
              time: shortTime(f.follow_up_time),
              pastDue: shortTime(f.follow_up_time) < nowHHMM,
            };
          });
        setList(rows);
      }
    } catch {
      setList([]);
    } finally {
      setListLoading(false);
    }
  };

  const toggle = (panel: Exclude<Panel, null>) => {
    if (open === panel) {
      setOpen(null);
      return;
    }
    setOpen(panel);
    loadList(panel);
  };

  const markRead = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await api.markNotificationRead(id);
      setList((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
      setCounts((c) => ({ ...c, notificationsUnread: Math.max(0, c.notificationsUnread - 1) }));
    } catch {
      /* ignore */
    }
  };

  // ---- Messages: emails, inbound texts, missed calls ----
  const dropItem = (m: any) => {
    setList((prev) => prev.filter((x) => x !== m));
    setCounts((c) => ({ ...c, messages: Math.max(0, c.messages - 1) }));
  };
  /** Mark ONE item read, from the dropdown — the same mark as on the file. */
  const markItemRead = async (e: React.MouseEvent, m: any) => {
    e.stopPropagation();
    try {
      if (m.kind === 'email') await api.markMessageSeen(m.linkId);
      else await api.markRingCentralSeen(m.kind === 'text' ? 'text' : 'call', m.id);
      dropItem(m);
      window.dispatchEvent(new Event('crm:badges-changed'));
    } catch {
      /* ignore — it stays in the list */
    }
  };
  /**
   * Open an item. NOTHING IS MARKED READ BY OPENING IT — emails, texts and
   * missed calls alike; only a Mark read button clears an item. An email or a
   * text / call on a lead opens on its Sales feed at the entry; one on no lead
   * opens the number and the unmatched choices. An "Add notes" call opens its
   * notes modal (the one the widget opens when a call ends); saving notes does
   * not mark it read either.
   */
  const openItem = (m: any) => {
    if (m.kind === 'email') { onOpenMessage(m.leadId, m.messageId); return; }
    if (m.kind === 'call_notes') {
      api.getRingCentralCall(m.id).then((r) => { if (r.data) setNotesFor(r.data); }).catch(() => undefined);
      return;
    }
    if (m.leadId) onOpenMessage(m.leadId, `${m.kind}:${m.id}`);
    else setLookup({ kind: m.kind, id: m.id });
  };

  const go = (fn: () => void) => {
    setOpen(null);
    fn();
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative' }}>
      {open && <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(null)} />}
      {lookupCallId && <MissedCallLookup callId={lookupCallId} onClose={() => setLookupCallId(null)} onOpenLead={onSelectLead} />}
      {lookup && <MissedCallLookup callId={lookup.id} kind={lookup.kind} onClose={() => setLookup(null)} onOpenLead={onSelectLead} />}
      {notesFor && (
        <CallNotesModal key={notesFor.call.id} entry={notesFor} onClose={(saved) => {
          // The feed line now carries the notes: tell an open lead file to refresh.
          if (saved) window.dispatchEvent(new CustomEvent('ringcentral:call-logged', { detail: { leadId: saved.call?.lead_id ?? null } }));
          setNotesFor(null);
        }} />
      )}

      <IconBtn Icon={Icons.newLead} label="New leads" count={counts.newLeads} active={open === 'newLeads'} onClick={() => toggle('newLeads')} />
      <IconBtn Icon={Icons.notification} label="Notifications" count={counts.notificationsUnread} active={open === 'notifications'} onClick={() => toggle('notifications')} />
      <IconBtn Icon={Icons.followUp} label="Follow-ups" count={followupBadge} active={open === 'followups'} onClick={() => toggle('followups')} />
      <IconBtn Icon={Icons.message} label="Messages" count={counts.messages} active={open === 'messages'} onClick={() => toggle('messages')} />

      {/* Follow-ups: three sections (General today / Overdue / Timed today). */}
      {open === 'followups' && (
        <Dropdown>
          <SectionBox onClick={() => go(onGoToFollowUps)}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--crm-text)' }}>Today's Follow-Ups</span>
            <CountPill n={counts.followupsGeneralToday + counts.followupsTimedToday} tone="amber" />
          </SectionBox>
          <SectionBox onClick={() => go(onGoToFollowUps)} danger={counts.followupsOverdue > 0}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: counts.followupsOverdue > 0 ? 'var(--crm-red)' : 'var(--crm-muted)' }}>Overdue follow-ups</span>
            <CountPill n={counts.followupsOverdue} tone={counts.followupsOverdue > 0 ? 'red' : 'muted'} />
          </SectionBox>

          <DropHead
            title="Today's scheduled"
            action={list.length > 15 ? { label: 'View all →', onClick: () => go(onGoToFollowUps) } : undefined}
          />
          {listLoading ? (
            <Empty>Loading…</Empty>
          ) : list.length === 0 ? (
            <Empty>All caught up — no scheduled calls left today.</Empty>
          ) : (
            list.slice(0, 15).map((r) => (
              <Row key={r.id} onClick={() => go(() => onSelectLead(r.lead_id))}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: 'var(--crm-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ color: 'var(--crm-dim)', fontFamily: "'DM Mono', monospace" }}>{r.job}</span> · {r.name}
                  </span>
                  <span style={{ fontSize: '11px', fontFamily: "'DM Mono', monospace", color: r.pastDue ? 'var(--crm-red)' : 'var(--crm-amber)', whiteSpace: 'nowrap' }}>
                    {r.time}{r.pastDue ? ' · past due' : ''}
                  </span>
                </div>
              </Row>
            ))
          )}
          {list.length > 15 && (
            <div style={{ textAlign: 'center', padding: '9px' }}>
              <button onClick={() => go(onGoToFollowUps)} style={{ background: 'none', border: 'none', color: 'var(--crm-accent)', cursor: 'pointer', fontSize: '11px' }}>View all {list.length} →</button>
            </div>
          )}
        </Dropdown>
      )}

      {(open === 'newLeads' || open === 'notifications') && (
        <Dropdown>
          <DropHead
            title={open === 'newLeads' ? 'New leads to claim' : 'Notifications'}
            action={
              open === 'newLeads'
                ? { label: 'Open New Leads →', onClick: () => go(onGoToNewLeads) }
                : { label: 'View all →', onClick: () => go(onGoToNotifications) }
            }
          />
          {listLoading ? (
            <Empty>Loading…</Empty>
          ) : list.length === 0 ? (
            <Empty>{open === 'notifications' ? 'No notifications.' : 'No unclaimed leads.'}</Empty>
          ) : (
            list.map((item) =>
              open === 'newLeads' ? (
                <Row key={item.id} onClick={() => go(() => onSelectLead(item.id))}>
                  <div style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '12px' }}>{item.first_name} {item.last_name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>{item.lead_source || 'No source'} · {item.move_type || '—'}</div>
                </Row>
              ) : open === 'notifications' ? (
                <Row key={item.id} onClick={() => go(() => {
                  if (item.lead_id) onSelectLead(item.lead_id);
                  // A missed call from a number on no lead: the number and a lead search.
                  else if (item.type === 'call_missed' && item.ringcentral_call_id) setLookupCallId(item.ringcentral_call_id);
                })}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: item.read_at ? 'transparent' : 'var(--crm-accent-fill)', marginTop: '5px', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '12px' }}>{item.title}</div>
                      {item.body && <div style={{ fontSize: '11px', color: 'var(--crm-muted)', marginTop: '1px' }}>{item.body}</div>}
                      <div style={{ fontSize: '10px', color: 'var(--crm-dim)', marginTop: '3px', fontFamily: "'DM Mono', monospace" }}>{formatDateTime(item.created_at)}</div>
                    </div>
                    {!item.read_at && (
                      <button onClick={(e) => markRead(e, item.id)} style={{ background: 'none', border: 'none', color: 'var(--crm-muted)', cursor: 'pointer', fontSize: '10px', padding: '2px 4px', whiteSpace: 'nowrap' }}>Mark read</button>
                    )}
                  </div>
                </Row>
              ) : null
            )
          )}
        </Dropdown>
      )}

      {open === 'messages' && (
        <Dropdown>
          {/* No "mark all read" here, deliberately. Clearing an unread mark means
              somebody has read the email, and one button that clears twenty of
              them cannot mean that — the more so because the mark is shared, so
              it would answer on behalf of everyone else watching those files.
              Each message is marked read on the message. */}
          <DropHead title="Messages" />
          {listLoading && <Empty>Loading…</Empty>}
          {!listLoading && list.length === 0 && <Empty>Nothing unread.</Empty>}
          {!listLoading && list.map((m) => {
            const ch = MSG_KIND[m.kind as keyof typeof MSG_KIND] ?? MSG_KIND.email;
            const key = m.kind === 'email' ? `e-${m.linkId}` : `${m.kind}-${m.id}`;
            return (
              <Row key={key} onClick={() => go(() => openItem(m))}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <span title={ch.label} style={{ display: 'inline-flex', color: ch.fg, background: ch.tint, border: `1px solid ${ch.line}`, borderRadius: '6px', padding: '3px', marginTop: '1px', flexShrink: 0 }}>
                    <ch.Icon size={13} aria-hidden="true" />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--crm-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.leadName || m.number}
                      </span>
                      {m.jobNo && <span style={{ fontSize: '11px', color: 'var(--crm-muted)', fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>{m.jobNo}</span>}
                      <span style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--crm-dim)', flexShrink: 0 }}>
                        {formatDateTimeInTz(m.sentAt, tz)}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: ch.fg, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.kind === 'email' ? `Email · ${m.fromName || m.fromAddress}` : `${ch.label} · ${m.number}`}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--crm-text)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.kind === 'email' ? m.subject : m.preview}
                    </div>
                    {m.kind === 'email' && (
                      <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.snippet}
                      </div>
                    )}
                  </div>
                  <button onClick={(e) => markItemRead(e, m)} style={{ background: 'none', border: 'none', color: 'var(--crm-muted)', cursor: 'pointer', fontSize: '10px', padding: '2px 4px', whiteSpace: 'nowrap', flexShrink: 0 }}>Mark read</button>
                </div>
              </Row>
            );
          })}
        </Dropdown>
      )}
    </div>
  );
}

// ---- presentational bits ---------------------------------------------------

/** Each Messages kind in its feed colours (--crm-comm-*, theme.css). */
const MSG_KIND = {
  email: { label: 'Email', Icon: Icons.email, fg: 'var(--crm-comm-email)', tint: 'var(--crm-comm-email-tint)', line: 'var(--crm-comm-email-line)' },
  text: { label: 'Text', Icon: Icons.message, fg: 'var(--crm-comm-text)', tint: 'var(--crm-comm-text-tint)', line: 'var(--crm-comm-text-line)' },
  call: { label: 'Missed call', Icon: Icons.phone, fg: 'var(--crm-comm-call)', tint: 'var(--crm-comm-call-tint)', line: 'var(--crm-comm-call-line)' },
  call_notes: { label: 'Call', Icon: Icons.phone, fg: 'var(--crm-comm-call)', tint: 'var(--crm-comm-call-tint)', line: 'var(--crm-comm-call-line)' },
} as const;

function IconBtn({ Icon, label, count, active, onClick }: { Icon: React.ComponentType<any>; label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      title={label}
      aria-label={label}
      onClick={onClick}
      style={{
        position: 'relative', width: '34px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: active ? 'var(--crm-panel-2)' : 'transparent', border: active ? '1px solid var(--crm-line)' : '1px solid transparent',
        borderRadius: '7px', cursor: 'pointer', color: 'var(--crm-text)',
      }}
    >
      <Icon size={18} aria-hidden="true" />
      {count > 0 && (
        <span style={{ position: 'absolute', top: '-3px', right: '-3px', minWidth: '15px', height: '15px', padding: '0 3px', borderRadius: '8px', background: 'var(--crm-red-fill)', color: 'var(--crm-on-fill)', fontSize: '9px', fontWeight: 700, lineHeight: '15px', textAlign: 'center' }}>
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}

const Dropdown: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ position: 'absolute', top: '38px', right: 0, width: '320px', maxHeight: '60vh', overflowY: 'auto', background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', boxShadow: '0 10px 30px var(--crm-shadow-color)', zIndex: 50 }}>
    {children}
  </div>
);

function DropHead({ title, action }: { title: string; action?: { label: string; onClick: () => void } }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--crm-line)', position: 'sticky', top: 0, background: 'var(--crm-panel)' }}>
      <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--crm-text)' }}>{title}</span>
      {action && <button onClick={action.onClick} style={{ background: 'none', border: 'none', color: 'var(--crm-accent)', cursor: 'pointer', fontSize: '11px', padding: 0 }}>{action.label}</button>}
    </div>
  );
}

const Row: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <div onClick={onClick} style={{ padding: '9px 12px', borderBottom: '1px solid var(--crm-panel-2)', cursor: 'pointer' }}>{children}</div>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: '20px 14px', textAlign: 'center', color: 'var(--crm-dim)', fontSize: '12px' }}>{children}</div>
);

// A clickable summary box (General / Overdue sections of the follow-up dropdown).
const SectionBox: React.FC<{ onClick: () => void; danger?: boolean; children: React.ReactNode }> = ({ onClick, danger, children }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
      padding: '11px 12px', cursor: 'pointer', borderBottom: '1px solid var(--crm-line)',
      background: danger ? 'var(--crm-red-tint)' : 'transparent',
    }}
  >
    {children}
  </div>
);

function CountPill({ n, tone }: { n: number; tone: 'amber' | 'red' | 'muted' }) {
  // Counts: red badge, white figure; amber reads as a warning pill; muted is a zero.
  const bg = tone === 'red' ? 'var(--crm-badge)' : tone === 'amber' ? 'var(--crm-amber-tint)' : 'var(--crm-panel-2)';
  const fg = tone === 'red' ? 'var(--crm-badge-text)' : tone === 'amber' ? 'var(--crm-amber)' : 'var(--crm-muted)';
  return (
    <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '11px', fontWeight: 700, background: bg, color: fg, borderRadius: '9px', padding: '1px 8px' }}>{n}</span>
  );
}
